import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { UserService } from '../shared/services/user.service';
import { firstValueFrom, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from '../authentications/authentication.service';
import { ActivatedRoute, Router } from "@angular/router";
import { OidcProviderName, SsoAuthService } from '../authentications/sso-auth.service';

@Component({
  selector: 'fspro-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
  standalone: false
})
export class SignInComponent implements OnInit, OnDestroy {

  private static readonly SSO_ERROR_TRANSLATION_PREFIX = 'fspro.front.authentication.sso.errors.';

  public readonly oidcProviders: ReadonlyArray<{ name: OidcProviderName; label: string; logo: string }> = [
    { name: 'google', label: 'Google', logo: '/assets/images/google-g-logo.png' },
    { name: 'eventival', label: 'Eventival', logo: '/assets/images/eventival-seeklogo-2.svg' },
  ];

  public loginForm: FormGroup;
  public isLoadingUserSignIn = false;
  public mustVerifyCode: boolean = false;
  public isResendingCode: boolean = false;
  public couldResendCode: boolean = false;
  public isLoginFailed: boolean;
  public loginErrorMessage: string | null = null;
  public verificationCodeForm: FormGroup;
  public isVerifyingCode: boolean = false;
  public isLoginAsGuest: boolean = false;
  public loginAsGuestForm: FormGroup;
  public isLoadingGuestSignIn: boolean = false;
  public displayLoginAsGuestMessage: boolean = false;
  public loginAsGuestErrorMessage: string | null = null;

  private readonly _destroy$ = new Subject();
  private _redirectUrl: string | null = null;
  private _rhGroupId: string | null = null;
  private _setIdentifier: string | null = null;
  private _hash: string | null = null;

  private readonly ssoAuthService = inject(SsoAuthService);

  constructor(
    private _fb: FormBuilder,
    private _userService: UserService,
    private _snackbar: MatSnackBar,
    public _authentication: AuthenticationService,
    private _translate: TranslateService,
    private readonly _router: Router,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loginForm = this._fb.group({
      username: ['', Validators.email],
      password: ['', Validators.required]
    });

    this.loginAsGuestForm = this._fb.group({
      username: ['', Validators.email],
    });

    this.verificationCodeForm = this._fb.group({
      verificationCode: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
    });

    this.route.params.subscribe({
      next: (params: any) => {
        if (params.rhGroupId && params.setIdentifier && params.hash) {
          this._rhGroupId = params.rhGroupId;
          this._setIdentifier = params.setIdentifier;
          this._hash = params.hash;
        }
      }
    });

    this.route.queryParams.subscribe(params => {
      this._redirectUrl = params['redirect'] || null;
      const email = params['email'] || null;
      const guest = params['guest'] || null;
      const ssoError = params['error'] || null;

      if (ssoError !== null) {
        const translationKey = SignInComponent.SSO_ERROR_TRANSLATION_PREFIX + ssoError;
        const translatedError = this._translate.instant(translationKey);
        this.loginErrorMessage = translatedError !== translationKey
          ? translatedError
          : this._translate.instant(SignInComponent.SSO_ERROR_TRANSLATION_PREFIX + 'sso_error');
        this.isLoginFailed = true;
      }

      if (guest === 'true') {
        this.isLoginAsGuest = true;
        this.loginAsGuestForm.get('username').setValue(email);
      } else {
        this.loginForm.get('username').setValue(email);
      }
    });
  }

  ngOnDestroy() {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  onLogin(): void {
    this.isLoadingUserSignIn = true;
    this.loginForm.disable();
    this._userService.checkUserDevice(this.loginForm.get('username').value, this.loginForm.get('password').value)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (data) => {
          if (!data.mustVerifyCode) {
            this._login();
          } else {
            this.mustVerifyCode = true;
          }
          if (this.isResendingCode) {
            this._snackbar.open(
              `Code successfully resent!`,
              'Close',
              {duration: 5000, verticalPosition: 'top', horizontalPosition: 'center', panelClass: 'info-snackbar'}
            );
          }
          this.couldResendCode = false;
          this.isResendingCode = false;
          this.isLoginFailed = false;
        },
        error: (error) => {
          console.error(error);
          this.loginErrorMessage = typeof error.error === 'string' ? error.error : error.error.message;
          this.isLoginFailed = true;
          this.isLoadingUserSignIn = false;
          this.mustVerifyCode = false;
          this.loginForm.enable();
        }
    });
  }

  private _login() {
    this._authentication.authenticate(this.loginForm.getRawValue())
      .pipe(
        finalize(() => {
          this.isLoadingUserSignIn = false;
          this.loginForm.enable();
        })
      )
      .subscribe({
        next: () => {
          firstValueFrom(this._userService.logUserAuthentication()).then(() => {
            this._router.navigateByUrl(this._redirectUrl).then();
          });
        },
        error: (error) => {
          this.loginErrorMessage = error.error.message !== 'Invalid credentials.' ?
            error.error.message + (error.error.message === 'Your device is not authorized.' ? '' : this._translate.instant('fspro.front.header.login_too_many.label.error')) :
            this._translate.instant('fspro.front.header.login.label.error');
          if (error.error.message !== 'Invalid credentials.') {
            console.log(error);
          }
          this.isLoginFailed = true;
        }
      });
  }

  public verifyCode() {
    this.isVerifyingCode = true;
    this._userService.verifyCode(
      this.verificationCodeForm.get('verificationCode').value,
      this.loginForm.get('username').value
    ).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => {
        if (data.success) {
          this.verificationCodeForm.reset();
          this.mustVerifyCode = false;
          this.isVerifyingCode = false;
          this.isLoginFailed = false;
          this.couldResendCode = false;
          this.isResendingCode = false;
          this._login();
        }
      },
      error: (error) => {
        this.isVerifyingCode = false;
        console.error('error', error.error);
        this.loginErrorMessage = typeof error.error === 'string' ? error.error : error.error.message;

        this.verificationCodeForm.get('verificationCode').setValue('');

        if (error.status && error.status === 410) {
          this.couldResendCode = true;
        }

        this.isLoginFailed = true;
      }
    });
  }

  onGoBack() {
    this.mustVerifyCode = false;
    this.isLoginFailed = false;
    this.loginForm.enable();
    this.loginForm.reset();
    this.verificationCodeForm.reset();
    this.isLoadingUserSignIn = false;
    this.isLoadingGuestSignIn = false;
    this.isLoginAsGuest = false;
    this.loginAsGuestForm.reset();
    this.displayLoginAsGuestMessage = false;
    this.loginAsGuestErrorMessage = null;
  }

  public async sendActivationEmail() {
    if (this.loginForm.get('username').valid) {
      const email = this.loginForm.get('username').value;

      await firstValueFrom(this._userService.requestActivateLink(email));

      this.loginErrorMessage = this._translate.instant('fspro.front.header.login.label.success_mail_sent');
    }
  }

  public loginAsGuest() {
    this.isLoadingGuestSignIn = true;
    this.displayLoginAsGuestMessage = null;
    this.loginAsGuestErrorMessage = null;
    this._userService.userLoginGuest(this.loginAsGuestForm.get('username').value, this._rhGroupId, this._setIdentifier, this._hash)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.isLoadingGuestSignIn = false;
          this.loginAsGuestForm.enable();
          this.displayLoginAsGuestMessage = true;
        },
        error: (error) => {
          console.error(error);
          this.isLoadingGuestSignIn = false;
          this.loginAsGuestErrorMessage = error.error.message;
        }
      });
  }

  loginWithOidc(provider: OidcProviderName): void {
    this.ssoAuthService.loginWithOidc(provider, this._redirectUrl);
  }
}
